import { CaseReducer, PayloadAction } from '@reduxjs/toolkit';
import { makeApiaUrl } from '../../api/apiaApi/apiaApi';
import { addBoundary } from '../../utils/number';
import { arrayOrArray, getJSObjfromXML } from '../../utils/util';
import processGridField, { checkIfRequiredColumns } from './processGridField';
import { TRowActionResponse } from './rows/types';
import { addColumnBoundary } from '../selection/setNewFocused';
import { IApiaDataGrid, IDataGridSlice, IMakeGridActionUrl } from './types';

export function makeGridActionUrl({ context, ...props }: IMakeGridActionUrl) {
  return makeApiaUrl({
    ajaxUrl: 'apia.execution.FormAction.run',
    action: 'processGridFieldAction',
    stringifyOptions: { arrayFormat: 'repeat' },
    frmId: context.form.formData.id,
    frmParent: context.form.frmParent,
    fldId: context.grid.field.id,
    fireAjaxEvt: true,
    react: true,
    ...props,
  });
}

export const putItLoading: CaseReducer<
  Record<string, IDataGridSlice>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  PayloadAction<any, any, { arg: { gridId: string } }>
> = (
  state,
  {
    meta: {
      arg: { gridId },
    },
  },
) => {
  state[gridId].loading++;
};

export const finishLoading: CaseReducer<
  Record<string, IDataGridSlice>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  PayloadAction<any, any, { arg: { gridId: string } }>
> = (
  state,
  {
    meta: {
      arg: { gridId },
    },
  },
) => {
  state[gridId].loading--;
};

export const processActions: CaseReducer<
  Record<string, IDataGridSlice>,
  PayloadAction<TRowActionResponse | null, string, { arg: { gridId: string } }>
> = (state, { meta, payload, type }) => {
  const { arg } = meta;
  const action = (type.match(/dataGridSlice\/(\w+)/) ?? [])[1];
  state[arg.gridId].loading--;
  state[arg.gridId].updateCellProps = undefined;
  state[arg.gridId].updating = false;
  if (!payload || !payload.xmlResponse.gridXml) {
    console.error(payload);
  } else {
    const gridXml = getJSObjfromXML(
      payload.xmlResponse.gridXml,
    ) as unknown as IApiaDataGrid;
    let newField = arrayOrArray(gridXml.field);
    if (
      !state[arg.gridId].context.grid.field.scriptEvents.find(
        (current) => current.evtName === 'gridAdd',
      ) &&
      !state[arg.gridId].context.grid.field.serverEvents.find(
        (current) => current.evtName === 'gridAdd',
      ) &&
      !state[arg.gridId].context.grid.field.properties.paged &&
      type.match(/addRow/)
    ) {
      (function appendRowAtTheEnd() {
        newField = newField.map((current, index) => {
          const newValue = current;
          if (newValue.row) {
            const currentFieldValues = arrayOrArray(
              arrayOrArray(state[arg.gridId].context.grid.field.field)[index]
                .row,
            ).map(() => ({ value: '__actionUtils__noUpdate__' }));
            newValue.row = [
              ...currentFieldValues,
              ...arrayOrArray(newValue.row),
            ];
          }
          if (newValue.fieldValue) {
            const currentFieldValues = arrayOrArray(
              arrayOrArray(state[arg.gridId].context.grid.field.field)[index]
                .fieldValue,
            ).map(() => ({
              value: '__actionUtils__noUpdate__',
            }));
            newValue.fieldValue = [
              ...currentFieldValues,
              ...arrayOrArray(newValue.fieldValue),
            ];
          }
          return newValue;
        });
      })();
    }
    state[arg.gridId] = processGridField(action, state[arg.gridId], {
      form: state[arg.gridId].context.form,
      grid: {
        ...state[arg.gridId].context.grid,
        field: {
          ...state[arg.gridId].context.grid.field,
          ...gridXml,
          field: newField,
        },
      },
    });
    state[arg.gridId].selectedRows = payload.newSelectedRows.map((el) =>
      addBoundary({
        number: el,
        min: 0,
        max: state[arg.gridId].rows.length - 1,
      }),
    );
    state[arg.gridId].focusedRow = addBoundary({
      number: payload.focusRow,
      min: 0,
      max: state[arg.gridId].rows.length - 1,
    });
    if (payload.focusColumn !== undefined) {
      state[arg.gridId].focusedColumn = addColumnBoundary(
        payload.focusColumn,
        state[arg.gridId],
      );
    }
    if (payload.focusGrid) state[arg.gridId].isFocused = true;
  }
  checkIfRequiredColumns(state[arg.gridId].context);
};
